import {
  DEFAULT_SERVANT_ID,
  DEFAULT_STAGE_ID,
  OPENING_IMPRESSIONS,
  getCard,
  getDevotion,
  getFreeBattleSupply,
  getServant,
  getStage,
  type CardDefinition,
  type ObjectCardDefinition,
} from "../content/index.js";
import { RULES } from "./rules.js";
import type {
  BoardPosition,
  CardInstance,
  CreateGameOptions,
  CreateOpenSupplyGameOptions,
  Depth,
  Direction,
  DreamObjectState,
  GameResult,
  GameState,
  HourResolution,
  InjectObjectOptions,
  Lane,
  PlanTarget,
  PlanValidation,
  PlayerIndex,
  PlayerPlan,
  PlayerPlanInput,
  PlayerState,
  ResolutionEvent,
  ResolutionEventType,
  ResolutionFrame,
  ResultCause,
  UnframedResolutionEvent,
  Winner,
} from "./types.js";

const PLAYER_INDICES = [0, 1] as const;
const LANES = [1, 2, 3] as const;

interface EventSink {
  push(...events: UnframedResolutionEvent[]): number;
}

interface EventQueue extends EventSink {
  readonly items: ResolutionEvent[];
}

interface AiCandidate {
  score: number;
  plan: PlayerPlan;
}

export type HourPlans = readonly [
  PlayerPlanInput | PlayerPlan | undefined,
  PlayerPlanInput | PlayerPlan | undefined,
];

export function createGame(options: CreateGameOptions = {}): GameState {
  const stage = getStage(options.stageId) ?? getStage(DEFAULT_STAGE_ID);
  if (!stage) throw new Error("Default stage definition is missing.");

  const state: GameState = {
    schemaVersion: 2,
    stageId: stage.id,
    turn: 1,
    firstPlayer: 0,
    nextCardInstanceId: 1,
    nextObjectId: 1,
    randomState: normalizeSeed(options.seed ?? Date.now()),
    board: [],
    suspendedDreams: [],
    players: [
      createPlayer(options.playerNames?.[0] || "東のしもべ", options.servantIds?.[0]),
      createPlayer(options.playerNames?.[1] || "西のしもべ", options.servantIds?.[1]),
    ],
    log: [],
    lastResolution: null,
    result: null,
    flags: {},
    stats: {
      cardsPlayed: [0, 0],
      passes: [0, 0],
      resonanceCount: 0,
      mergeCount: 0,
      fizzleCount: 0,
      stageTriggerCount: 0,
      devotionsUsed: [0, 0],
    },
  };

  for (const playerIndex of PLAYER_INDICES) {
    const deckOrder = createDeckOrder(stage.deck);
    shuffle(state, deckOrder);
    state.players[playerIndex].deck = instantiateDeck(state, deckOrder);
    drawCards(state, playerIndex, RULES.initialHand);
  }
  state.log.push("二つの《窓》が重なり、《" + stage.name + "》が映し出された。");
  return state;
}

export function createOpenSupplyGame(options: CreateOpenSupplyGameOptions = {}): GameState {
  const state = createGame(options);
  for (const player of state.players) {
    player.deck = [];
    player.hand = [];
    player.discard = [];
  }
  state.openSupply = {
    energy: [RULES.openSupplyInitialEnergy, RULES.openSupplyInitialEnergy],
    recovery: RULES.openSupplyRecovery,
    maxEnergy: RULES.openSupplyMaxEnergy,
    cards: getFreeBattleSupply(state.stageId).map((entry) => ({
      cardId: entry.cardId,
      cost: entry.cost,
      limited: entry.limited,
      availableTo: [true, true],
    })),
    openingObjectCardId: "",
  };

  let openingObjectCardId = options.openingObjectCardId;
  if (openingObjectCardId === undefined || openingObjectCardId === "random") {
    const index = Math.floor(nextRandom(state) * OPENING_IMPRESSIONS.length);
    openingObjectCardId = OPENING_IMPRESSIONS[index]?.cardId ?? null;
  }
  if (openingObjectCardId) {
    const opening = injectObject(state, openingObjectCardId, {
      lane: 2,
      depth: 2,
      owner: null,
      isOpeningImpression: true,
    });
    if (opening) {
      state.openSupply.openingObjectCardId = openingObjectCardId;
      state.log.push("《窓》には、《" + opening.name + "》が最初から浮かんでいた。");
    }
  }
  return state;
}

function createPlayer(name: string, servantId?: string): PlayerState {
  return {
    name,
    servantId: getServant(servantId)?.id ?? DEFAULT_SERVANT_ID,
    devotionUsed: false,
    happiness: RULES.initialHappiness,
    focusLane: 2,
    focusLockedUntilTurn: 0,
    lastPlayedCardId: "",
    lastPlayedTurn: 0,
    deck: [],
    hand: [],
    discard: [],
  };
}

function createDeckOrder(recipe: Readonly<Record<string, number>>): string[] {
  const cardIds: string[] = [];
  for (const [cardId, count] of Object.entries(recipe)) {
    for (let index = 0; index < count; index += 1) cardIds.push(cardId);
  }
  return cardIds;
}

function instantiateDeck(state: GameState, cardIds: readonly string[]): CardInstance[] {
  return cardIds.map((cardId) => ({
    instanceId: "card-" + state.nextCardInstanceId++,
    cardId,
  }));
}

function drawCards(state: GameState, playerIndex: PlayerIndex, count: number): void {
  const player = state.players[playerIndex];
  for (let index = 0; index < count && player.deck.length > 0; index += 1) {
    const card = player.deck.shift();
    if (card) player.hand.push(card);
  }
}

export function legalFocusLanes(state: GameState, playerIndex: PlayerIndex): Lane[] {
  const player = state.players[playerIndex];
  const current = player.focusLane;
  if (player.focusLockedUntilTurn >= state.turn) return [current];
  return LANES.filter((lane) => Math.abs(lane - current) <= 1);
}

export function getCardTargets(state: GameState, cardId: string): PlanTarget[] {
  const card = getCard(cardId);
  if (!card) return [];
  if (card.kind === "object") {
    return LANES
      .filter((lane) => !card.captureLaneImpactMax || laneImpact(state, lane) !== 0)
      .map((lane) => ({ lane }));
  }
  if (card.targetType === "none") return state.board.length > 0 ? [{}] : [];
  if (card.targetType === "lane") {
    return LANES
      .filter((lane) => card.effect !== "homeward" || state.board.some((object) => object.lane === lane))
      .map((lane) => ({ lane }));
  }
  if (card.targetType === "depth") {
    return ([1, 2, 3] as const)
      .filter((depth) => card.effect === "harmony"
        ? state.board.some((object) => object.depth === depth && object.lane !== 2)
        : state.board.some((object) => object.depth === depth))
      .map((depth) => ({ depth }));
  }
  if (card.targetType === "object-direction") {
    return state.board.flatMap((object) => [
      { objectId: object.id, direction: -1 as Direction },
      { objectId: object.id, direction: 1 as Direction },
    ]);
  }
  if (card.targetType === "object-depth") {
    return state.board.flatMap((object) => ([object.depth - 1, object.depth + 1])
      .filter(isDepth)
      .map((depth) => ({ objectId: object.id, depth })));
  }
  return state.board
    .filter((object) => card.effect !== "return-deep" || object.depth < RULES.maxDepth)
    .map((object) => ({ objectId: object.id }));
}

export function getDevotionTargets(state: GameState, playerIndex: PlayerIndex): PlanTarget[] {
  const player = state.players[playerIndex];
  const servant = getServant(player.servantId);
  if (!servant || player.devotionUsed) return [];
  const devotion = servant.devotion;
  if (devotion.targetType === "player") {
    return PLAYER_INDICES
      .filter((targetIndex) => state.players[targetIndex].happiness !== RULES.idealHappiness)
      .map((targetIndex) => ({ playerIndex: targetIndex }));
  }
  if (devotion.targetType === "replay") {
    const card = getReplayableCard(state, playerIndex);
    return card ? getCardTargets(state, card.id) : [];
  }
  if (devotion.targetType === "object") {
    return state.board
      .filter((object) => devotion.effect !== "ignite" || canIgnite(object))
      .map((object) => ({ objectId: object.id }));
  }
  return [{}];
}

export function getReplayableCard(
  state: GameState,
  playerIndex: PlayerIndex,
): CardDefinition | null {
  const player = state.players[playerIndex];
  if (player.lastPlayedTurn !== state.turn - 1) return null;
  return getCard(player.lastPlayedCardId);
}

export function validatePlan(
  state: GameState,
  playerIndex: PlayerIndex,
  plan: PlayerPlanInput | PlayerPlan | null | undefined,
): PlanValidation {
  if (!plan || !legalFocusLanes(state, playerIndex).includes(Number(plan.focusLane) as Lane)) {
    return { ok: false, reason: "注目できないレーンです。" };
  }

  const player = state.players[playerIndex];
  const selectedActionCount = Number(Boolean(plan.cardInstanceId))
    + Number(Boolean(plan.supplyCardId))
    + Number(Boolean(plan.devotionId));
  if (selectedActionCount > 1) {
    return { ok: false, reason: "カードと献身は同時に使えません。" };
  }

  if (plan.devotionId) {
    const servant = getServant(player.servantId);
    if (!servant || servant.devotion.id !== plan.devotionId) {
      return { ok: false, reason: "このしもべの献身ではありません。" };
    }
    if (player.devotionUsed) return { ok: false, reason: "献身はこの夜に使用済みです。" };
    if (servant.devotion.targetType === "player") {
      const targetIndex = Number(plan.target?.playerIndex);
      if (targetIndex !== 0 && targetIndex !== 1) {
        return { ok: false, reason: "献身を向けるご主人を選んでください。" };
      }
      return state.players[targetIndex].happiness === RULES.idealHappiness
        ? { ok: false, reason: "幸福度15のご主人には、注ぐ方向がありません。" }
        : { ok: true };
    }
    if (servant.devotion.targetType === "replay") {
      const replayCard = getReplayableCard(state, playerIndex);
      if (!replayCard) {
        return { ok: false, reason: "直前の時間に再演できるカードがありません。" };
      }
      return validateCardTarget(state, replayCard, plan.target);
    }
    if (servant.devotion.targetType === "object") {
      const target = state.board.find((object) => object.id === plan.target?.objectId);
      if (!target) return { ok: false, reason: "献身を向ける夢オブジェクトを選んでください。" };
      if (servant.devotion.effect === "ignite"
        && !canIgnite(target)) {
        return { ok: false, reason: "《口火を切る》は、絶対値1〜3の夢へ使えます。" };
      }
      return { ok: true };
    }
    return { ok: true };
  }

  if (plan.supplyCardId) {
    const supply = state.openSupply;
    if (!supply) return { ok: false, reason: "公開札を使う試合ではありません。" };
    const entry = supply.cards.find((item) => item.cardId === plan.supplyCardId);
    if (!entry) return { ok: false, reason: "そのカードは演出卓にありません。" };
    if (!entry.availableTo[playerIndex]) {
      return { ok: false, reason: "その特殊札はこの夜に使用済みです。" };
    }
    if (supply.energy[playerIndex] < entry.cost) {
      return { ok: false, reason: "演出力が足りません。" };
    }
    const card = getCard(entry.cardId);
    return card
      ? validateCardTarget(state, card, plan.target)
      : { ok: false, reason: "カード定義がありません。" };
  }

  if (!plan.cardInstanceId) return { ok: true };

  const instance = player.hand.find((item) => item.instanceId === plan.cardInstanceId);
  if (!instance) return { ok: false, reason: "そのカードは手札にありません。" };
  const card = getCard(instance.cardId);
  if (!card) return { ok: false, reason: "カード定義がありません。" };

  return validateCardTarget(state, card, plan.target);
}

function validateCardTarget(
  state: GameState,
  card: CardDefinition,
  target: PlayerPlanInput["target"] | PlanTarget | undefined,
): PlanValidation {
  if (card.kind === "object") {
    const lane = Number(target?.lane);
    if (!isLane(lane)) return { ok: false, reason: "置くレーンを選んでください。" };
    if (card.captureLaneImpactMax && laneImpact(state, lane) === 0) {
      return { ok: false, reason: "《七つ目の音》へ写し取れる影響がないレーンです。" };
    }
    return { ok: true };
  }

  if (card.targetType === "none") {
    return state.board.length > 0
      ? { ok: true }
      : { ok: false, reason: "動かせる夢オブジェクトがありません。" };
  }
  if (card.targetType === "lane") {
    const lane = Number(target?.lane);
    if (!isLane(lane)) return { ok: false, reason: "対象のレーンを選んでください。" };
    if (card.effect === "homeward" && !state.board.some((object) => object.lane === lane)) {
      return { ok: false, reason: "そのレーンには帰らせる夢がありません。" };
    }
    return { ok: true };
  }
  if (card.targetType === "depth") {
    const depth = Number(target?.depth);
    if (!isDepth(depth)) return { ok: false, reason: "対象の深度を選んでください。" };
    const hasTarget = card.effect === "harmony"
      ? state.board.some((object) => object.depth === depth && object.lane !== 2)
      : state.board.some((object) => object.depth === depth);
    return hasTarget
      ? { ok: true }
      : { ok: false, reason: "その深度には対象となる夢がありません。" };
  }

  const targetObject = state.board.find((object) => object.id === target?.objectId);
  if (!targetObject) return { ok: false, reason: "対象の夢オブジェクトを選んでください。" };
  if (card.targetType === "object-direction" && !isDirection(Number(target?.direction))) {
    return { ok: false, reason: "風を送る向きを選んでください。" };
  }
  if (card.targetType === "object-depth") {
    const depth = Number(target?.depth);
    if (!isDepth(depth) || Math.abs(depth - targetObject.depth) !== 1) {
      return { ok: false, reason: "同じレーンの隣り合う深度を選んでください。" };
    }
  }
  if (card.effect === "return-deep" && targetObject.depth >= RULES.maxDepth) {
    return { ok: false, reason: "その夢はすでにもっとも深い場所にあります。" };
  }
  return { ok: true };
}

export function resolveHour(state: GameState, plans?: HourPlans): HourResolution | null {
  if (state.result) return state.lastResolution;

  const normalizedPlans: [PlayerPlan, PlayerPlan] = [
    normalizePlan(state, 0, plans?.[0]),
    normalizePlan(state, 1, plans?.[1]),
  ];
  const cardIds: [string, string] = [
    getPlanCardId(state, 0, normalizedPlans[0]),
    getPlanCardId(state, 1, normalizedPlans[1]),
  ];
  const devotionIds: [string, string] = [
    normalizedPlans[0].devotionId,
    normalizedPlans[1].devotionId,
  ];
  const targetLabels: [string, string] = [
    describePlanTarget(state, 0, normalizedPlans[0], cardIds[0], devotionIds[0]),
    describePlanTarget(state, 1, normalizedPlans[1], cardIds[1], devotionIds[1]),
  ];
  const initialFrame = captureResolutionFrame(state);
  const events = createEventQueue(state);
  const order: [PlayerIndex, PlayerIndex] = state.firstPlayer === 0 ? [0, 1] : [1, 0];

  state.players[0].focusLane = normalizedPlans[0].focusLane;
  state.players[1].focusLane = normalizedPlans[1].focusLane;
  events.push(createEvent("reveal", state.players[order[0]].name + "が先に夢へ触れる。"));

  for (const playerIndex of order) {
    resolvePlannedAction(state, playerIndex, normalizedPlans[playerIndex], events);
  }

  applyGuidance(state, events);

  const resonance = state.players[0].focusLane === state.players[1].focusLane;
  if (resonance) {
    state.stats.resonanceCount += 1;
    events.push(createEvent(
      "resonance",
      "両方の視線がレーン" + state.players[0].focusLane + "で重なり、夢が共鳴した。",
    ));
  }

  applyStageBeforeImpact(state, events, resonance);
  const impactBreakdowns: [number[], number[]] = [
    focusedImpactBreakdown(state, 0, resonance),
    focusedImpactBreakdown(state, 1, resonance),
  ];
  const impacts: [number, number] = [
    impactBreakdowns[0].reduce((sum, value) => sum + value, 0),
    impactBreakdowns[1].reduce((sum, value) => sum + value, 0),
  ];

  for (const playerIndex of PLAYER_INDICES) {
    const player = state.players[playerIndex];
    const before = player.happiness;
    player.happiness += impacts[playerIndex];
    events.push(createEvent("happiness", player.name + ": 幸福度 " + before + " → " + player.happiness, {
      playerIndex,
      before,
      after: player.happiness,
      impact: impacts[playerIndex],
      impactBreakdown: impactBreakdowns[playerIndex],
    }));
  }

  clearHourDevotionEffects(state);

  checkNightmare(state, events);
  if (!state.result) {
    applyStageAfterImpact(state, events);
    driftDreams(state, events);
    if (state.turn >= RULES.maxTurns) {
      finishMorning(state, events);
    } else {
      state.turn += 1;
      state.firstPlayer = state.firstPlayer === 0 ? 1 : 0;
      returnSuspendedDreams(state, events);
      if (state.openSupply) {
        recoverOpenSupplyEnergy(state);
      } else {
        drawCards(state, 0, RULES.drawPerTurn);
        drawCards(state, 1, RULES.drawPerTurn);
      }
    }
  }

  const resolution: HourResolution = {
    turn: state.turn,
    initialFrame,
    plans: normalizedPlans,
    cardIds,
    devotionIds,
    targetLabels,
    order,
    impacts,
    resonance,
    events: events.items,
  };
  state.lastResolution = resolution;
  for (const item of events.items) state.log.push(item.text);
  return resolution;
}

function focusedImpactBreakdown(
  state: GameState,
  playerIndex: PlayerIndex,
  resonance: boolean,
): number[] {
  const lane = state.players[playerIndex].focusLane;
  const objects = contributingObjectsInLane(state, lane);
  const breakdown = objects.map((object) => objectValue(object));
  const guided = objects.find((object) => object.guidedFor[playerIndex]);
  if (guided) breakdown.push(Math.sign(objectValue(guided)));
  if (!resonance) return breakdown;

  const raw = breakdown.reduce((sum, value) => sum + value, 0);
  breakdown.push(Math.sign(raw));
  for (const object of objects) breakdown.push(object.resonanceBonus);
  return breakdown;
}

function normalizePlan(
  state: GameState,
  playerIndex: PlayerIndex,
  plan: PlayerPlanInput | PlayerPlan | undefined,
): PlayerPlan {
  const fallbackFocus = state.players[playerIndex].focusLane;
  const candidate: PlayerPlanInput = {
    focusLane: Number(plan?.focusLane) || fallbackFocus,
    cardInstanceId: plan?.cardInstanceId || "",
    supplyCardId: plan?.supplyCardId || "",
    devotionId: plan?.devotionId || "",
    target: plan?.target ? { ...plan.target } : {},
  };
  if (!validatePlan(state, playerIndex, candidate).ok) return passPlan(fallbackFocus);

  const target: PlanTarget = {};
  const lane = Number(candidate.target?.lane);
  const depth = Number(candidate.target?.depth);
  const direction = Number(candidate.target?.direction);
  const targetPlayerIndex = Number(candidate.target?.playerIndex);
  if (isLane(lane)) target.lane = lane;
  if (isDepth(depth)) target.depth = depth;
  if (candidate.target?.objectId) target.objectId = candidate.target.objectId;
  if (isDirection(direction)) target.direction = direction;
  if (targetPlayerIndex === 0 || targetPlayerIndex === 1) {
    target.playerIndex = targetPlayerIndex;
  }
  const normalized: PlayerPlan = {
    focusLane: Number(candidate.focusLane) as Lane,
    cardInstanceId: candidate.cardInstanceId || "",
    devotionId: candidate.devotionId || "",
    target,
  };
  if (candidate.supplyCardId) normalized.supplyCardId = candidate.supplyCardId;
  return normalized;
}

function passPlan(focusLane: Lane): PlayerPlan {
  return { focusLane, cardInstanceId: "", devotionId: "", target: {} };
}

function getPlanCardId(state: GameState, playerIndex: PlayerIndex, plan: PlayerPlan): string {
  if (plan.supplyCardId) return plan.supplyCardId;
  if (!plan.cardInstanceId) return "";
  return state.players[playerIndex].hand.find(
    (item) => item.instanceId === plan.cardInstanceId,
  )?.cardId ?? "";
}

function describePlanTarget(
  state: GameState,
  playerIndex: PlayerIndex,
  plan: PlayerPlan,
  cardId: string,
  devotionId: string,
): string {
  const devotion = getDevotion(devotionId);
  if (devotion) {
    if (devotion.targetType === "player") {
      const targetIndex = plan.target.playerIndex;
      return targetIndex === 0 || targetIndex === 1
        ? state.players[targetIndex].name + "へ"
        : "対象を失ったご主人";
    }
    if (devotion.targetType === "replay") {
      const replayCard = getReplayableCard(state, playerIndex);
      if (!replayCard) return "再演する場面を失った";
      return describeCardTarget(state, replayCard, plan.target);
    }
    if (devotion.targetType !== "object") return "自分自身へ";
    const object = state.board.find((item) => item.id === plan.target.objectId);
    return object ? object.name + "へ" : "対象を失った夢";
  }

  const card = getCard(cardId);
  if (!card) return "";
  return describeCardTarget(state, card, plan.target);
}

function describeCardTarget(
  state: GameState,
  card: CardDefinition,
  target: PlanTarget,
): string {
  if (card.kind === "object") return "レーン" + target.lane + " / 深度3";
  if (card.targetType === "none") return "盤面全体へ";
  if (card.targetType === "lane") return "レーン" + target.lane + "へ";
  if (card.targetType === "depth") return "深度" + target.depth + "へ";
  const object = state.board.find((item) => item.id === target.objectId);
  if (!object) return "対象を失った夢";
  if (card.targetType === "object-direction") {
    return object.name + "を" + (Number(target.direction) < 0 ? "左" : "右") + "へ";
  }
  if (card.targetType === "object-depth") {
    return object.name + "を深度" + target.depth + "へ";
  }
  return object.name + "へ";
}

function resolvePlannedAction(
  state: GameState,
  playerIndex: PlayerIndex,
  plan: PlayerPlan,
  events: EventSink,
): void {
  if (plan.devotionId) {
    resolveDevotion(state, playerIndex, plan, events);
    return;
  }
  if (plan.supplyCardId) {
    resolveSupplyCard(state, playerIndex, plan, events);
    return;
  }
  if (!plan.cardInstanceId) {
    state.stats.passes[playerIndex] += 1;
    events.push(createEvent("pass", state.players[playerIndex].name + "は夢へ新しいものを加えなかった。", {
      playerIndex,
    }));
    return;
  }

  const player = state.players[playerIndex];
  const handIndex = player.hand.findIndex((item) => item.instanceId === plan.cardInstanceId);
  if (handIndex < 0) return;
  const instance = player.hand.splice(handIndex, 1)[0];
  if (!instance) return;
  player.discard.push(instance);
  state.stats.cardsPlayed[playerIndex] += 1;
  const card = getCard(instance.cardId);
  if (!card) return;
  player.lastPlayedCardId = card.id;
  player.lastPlayedTurn = state.turn;
  events.push(createEvent("card", player.name + ": 《" + card.name + "》", {
    playerIndex,
    cardId: card.id,
  }));
  applyCard(state, playerIndex, card, plan.target, events);
}

function resolveSupplyCard(
  state: GameState,
  playerIndex: PlayerIndex,
  plan: PlayerPlan,
  events: EventSink,
): void {
  const supply = state.openSupply;
  const entry = supply?.cards.find((item) => item.cardId === plan.supplyCardId);
  const card = getCard(plan.supplyCardId ?? "");
  if (!supply || !entry || !card || !entry.availableTo[playerIndex]) return;
  if (supply.energy[playerIndex] < entry.cost) return;

  const before = supply.energy[playerIndex];
  supply.energy[playerIndex] -= entry.cost;
  if (entry.limited) entry.availableTo[playerIndex] = false;
  state.stats.cardsPlayed[playerIndex] += 1;
  state.players[playerIndex].lastPlayedCardId = card.id;
  state.players[playerIndex].lastPlayedTurn = state.turn;
  events.push(createEvent(
    "card",
    state.players[playerIndex].name + ": 《" + card.name + "》（演出力" + entry.cost + "）",
    {
      playerIndex,
      cardId: card.id,
      before,
      after: supply.energy[playerIndex],
    },
  ));
  applyCard(state, playerIndex, card, plan.target, events);
}

function recoverOpenSupplyEnergy(state: GameState): void {
  const supply = state.openSupply;
  if (!supply) return;
  for (const playerIndex of PLAYER_INDICES) {
    supply.energy[playerIndex] = Math.min(
      supply.maxEnergy,
      supply.energy[playerIndex] + supply.recovery,
    );
  }
}

function resolveDevotion(
  state: GameState,
  playerIndex: PlayerIndex,
  plan: PlayerPlan,
  events: EventSink,
): void {
  const player = state.players[playerIndex];
  const servant = getServant(player.servantId);
  if (!servant || servant.devotion.id !== plan.devotionId || player.devotionUsed) return;

  const devotion = servant.devotion;
  player.devotionUsed = true;
  state.stats.devotionsUsed[playerIndex] += 1;
  events.push(createEvent("devotion", player.name + "は献身《" + devotion.name + "》を捧げた。", {
    playerIndex,
    servantId: servant.id,
    devotionId: devotion.id,
  }));

  if (devotion.effect === "comfort") {
    const targetIndex = plan.target.playerIndex;
    if (targetIndex !== 0 && targetIndex !== 1) return;
    const targetPlayer = state.players[targetIndex];
    const before = targetPlayer.happiness;
    const direction = Math.sign(RULES.idealHappiness - before);
    if (direction === 0) {
      state.stats.fizzleCount += 1;
      events.push(createEvent("fizzle", devotion.name + "は、すでに満ちた幸福へ注げなかった。", {
        devotionId: devotion.id,
      }));
      return;
    }
    targetPlayer.happiness += direction * 3;
    targetPlayer.focusLockedUntilTurn = Math.max(
      targetPlayer.focusLockedUntilTurn,
      state.turn + 1,
    );
    events.push(createEvent(
      "comfort",
      devotion.name + ": " + targetPlayer.name + "の幸福度 " + before + " → "
        + targetPlayer.happiness + "。次の時間、その視線は同じレーンに留まる。",
      {
        playerIndex: targetIndex,
        targetPlayerIndex: targetIndex,
        before,
        after: targetPlayer.happiness,
      },
    ));
    return;
  }

  if (devotion.effect === "replay") {
    const replayCard = getReplayableCard(state, playerIndex);
    if (!replayCard) {
      state.stats.fizzleCount += 1;
      events.push(createEvent("fizzle", devotion.name + "には再演できる前の場面がなかった。", {
        devotionId: devotion.id,
      }));
      return;
    }
    events.push(createEvent(
      "replay",
      devotion.name + ": 《" + replayCard.name + "》がもう一度映し出された。",
      { playerIndex, devotionId: devotion.id, cardId: replayCard.id },
    ));
    applyCard(state, playerIndex, replayCard, plan.target, events);
    return;
  }

  const object = state.board.find((item) => item.id === plan.target.objectId);
  if (!object) {
    state.stats.fizzleCount += 1;
    events.push(createEvent("fizzle", devotion.name + "は触れる夢を失った。", {
      devotionId: devotion.id,
    }));
    return;
  }

  if (devotion.effect === "suspend") {
    const from: BoardPosition = { lane: object.lane, depth: object.depth };
    removeObject(state, object);
    state.suspendedDreams.push({
      object,
      returnTurn: state.turn + 1,
      sourceName: devotion.name,
    });
    events.push(createEvent(
      "suspend",
      devotion.name + ": " + object.name + "を次の時間まで預かった。",
      { playerIndex, devotionId: devotion.id, objectId: object.id, from },
    ));
    return;
  }
  if (devotion.effect === "discover") {
    const grantedCard = getCard(devotion.grantedCardId ?? "");
    if (!grantedCard) {
      state.stats.fizzleCount += 1;
      events.push(createEvent("fizzle", devotion.name + "は記憶の中から何も見つけられなかった。", {
        devotionId: devotion.id,
      }));
      return;
    }
    player.hand.push({
      instanceId: "card-" + state.nextCardInstanceId++,
      cardId: grantedCard.id,
    });
    events.push(createEvent(
      "discover",
      devotion.name + ": " + object.name + "の記憶から《" + grantedCard.name + "》を見つけた。",
      { playerIndex, objectId: object.id, cardId: grantedCard.id },
    ));
    return;
  }
  if (devotion.effect === "ignite") {
    const current = objectValueBeforeTemporaryMultiplier(object, object.depth);
    const bonus = Math.max(0, Math.min(2, 4 - Math.abs(current)));
    if (current === 0 || bonus === 0) {
      state.stats.fizzleCount += 1;
      events.push(createEvent("fizzle", devotion.name + "は火の移る兆しを見つけられなかった。", {
        devotionId: devotion.id,
        objectId: object.id,
      }));
      return;
    }
    object.temporaryPotency += bonus;
    events.push(createEvent(
      "ignite",
      devotion.name + ": " + object.name + "の影響がこの時間だけ"
        + signed(bonus) + "ぶん燃え上がった。",
      { playerIndex, devotionId: devotion.id, objectId: object.id, value: bonus },
    ));
    return;
  }
  if (devotion.effect === "frame") {
    for (const laneObject of state.board) {
      if (laneObject.lane === object.lane) laneObject.framed = 0;
    }
    object.framed = 1;
    events.push(createEvent(
      "frame",
      devotion.name + ": " + object.name + "だけが額の内側へ収められた。",
      { playerIndex, devotionId: devotion.id, objectId: object.id, lane: object.lane },
    ));
    return;
  }
  if (devotion.effect === "preserve") {
    const preservedValue = objectValue(object);
    object.fixedValue = preservedValue;
    object.multiplier = 1;
    object.potency = 0;
    object.temporaryPotency = 0;
    object.temporaryMultiplier = 1;
    object.silenced = 0;
    events.push(createEvent(
      "preserve",
      devotion.name + ": " + object.name + "へ" + signed(preservedValue) + "の札を付けた。",
      {
        playerIndex,
        devotionId: devotion.id,
        objectId: object.id,
        value: preservedValue,
      },
    ));
    return;
  }
  if (devotion.effect === "guide") {
    object.guidedFor[playerIndex] = true;
    events.push(createEvent(
      "guidance",
      devotion.name + ": " + object.name + "の向かう先へ針を合わせた。",
      { playerIndex, devotionId: devotion.id, objectId: object.id },
    ));
  }
}

function applyCard(
  state: GameState,
  playerIndex: PlayerIndex,
  card: CardDefinition,
  target: PlanTarget,
  events: EventSink,
): void {
  if (card.kind === "object") {
    placeObject(state, playerIndex, card, Number(target.lane), events);
    return;
  }

  if (card.effect === "homeward") {
    const lane = Number(target.lane);
    if (!isLane(lane)) return;
    moveObjectsSimultaneously(
      state,
      state.board.filter((item) => item.lane === lane),
      (item) => ({ lane: item.lane, depth: 1 }),
      card.name,
      events,
    );
    return;
  }
  if (card.effect === "freeze-depth") {
    const depth = Number(target.depth);
    if (!isDepth(depth)) return;
    const objects = state.board.filter((item) => item.depth === depth);
    for (const item of objects) item.frozen = Math.max(item.frozen, 1);
    events.push(createEvent(
      "freeze",
      card.name + ": 深度" + depth + "の夢が雪の下へ留まった。",
      { cardId: card.id, depth },
    ));
    return;
  }
  if (card.effect === "rewind-all") {
    moveObjectsSimultaneously(
      state,
      [...state.board],
      (item) => ({
        lane: item.lane,
        depth: Math.min(RULES.maxDepth, item.depth + 1) as Depth,
      }),
      card.name,
      events,
    );
    return;
  }
  if (card.effect === "harmony") {
    const depth = Number(target.depth);
    if (!isDepth(depth)) return;
    moveObjectsSimultaneously(
      state,
      state.board.filter((item) => item.depth === depth && item.lane !== 2),
      () => ({ lane: 2, depth }),
      card.name,
      events,
    );
    return;
  }

  const object = state.board.find((item) => item.id === target.objectId);
  if (!object) {
    state.stats.fizzleCount += 1;
    events.push(createEvent("fizzle", card.name + "は触れる夢を失った。", { cardId: card.id }));
    return;
  }

  switch (card.effect) {
    case "wind":
      moveObject(
        state,
        object,
        object.lane + Number(target.direction),
        object.depth,
        card.name,
        events,
      );
      break;
    case "mirror":
      object.multiplier *= -1;
      events.push(createEvent("invert", object.name + "の意味が反転した。", { objectId: object.id }));
      break;
    case "sink":
      if (object.depth >= RULES.maxDepth) {
        state.stats.fizzleCount += 1;
        events.push(createEvent("fizzle", object.name + "はこれ以上深く沈まない。", {
          objectId: object.id,
        }));
      } else {
        moveObject(state, object, object.lane, object.depth + 1, card.name, events);
      }
      break;
    case "rise":
      moveObject(state, object, object.lane, object.depth - 1, card.name, events);
      break;
    case "return-deep":
      moveObject(state, object, object.lane, RULES.maxDepth, card.name, events);
      break;
    case "hush":
      object.frozen = Math.max(object.frozen, 1);
      events.push(createEvent("freeze", object.name + "は静かな幕に留められた。", {
        objectId: object.id,
      }));
      break;
    case "grow":
      changePotency(object, 1, card.name, events);
      break;
    case "prune":
      changePotency(object, -1, card.name, events);
      break;
    case "turnaround":
      if (object.lane === 2) {
        object.multiplier *= -1;
        events.push(createEvent("invert", card.name + ": " + object.name + "は中央で意味を反転した。", {
          objectId: object.id,
        }));
      } else {
        const outsideLane = object.lane === 1 ? 0 : RULES.laneCount + 1;
        moveObject(state, object, outsideLane, object.depth, card.name, events);
      }
      break;
    case "rewrap": {
      const from: BoardPosition = { lane: object.lane, depth: object.depth };
      removeObject(state, object);
      object.depth = RULES.maxDepth as Depth;
      state.suspendedDreams.push({
        object,
        returnTurn: state.turn + 1,
        sourceName: card.name,
      });
      events.push(createEvent(
        "suspend",
        card.name + ": " + object.name + "を包み直し、次の時間まで夢の外へ置いた。",
        { cardId: card.id, objectId: object.id, from },
      ));
      break;
    }
    case "encore":
      object.temporaryMultiplier *= 2;
      events.push(createEvent(
        "grow",
        card.name + ": " + object.name + "の影響をこの時間だけ二度響かせる。",
        { cardId: card.id, objectId: object.id },
      ));
      break;
    case "layer-time": {
      const depth = Number(target.depth);
      if (!isDepth(depth)) break;
      const copy = copyObjectAt(state, object, depth);
      const occupant = findAt(state, object.lane, depth);
      if (occupant) {
        mergeObjects(state, copy, occupant, object.lane, depth, card.name, events, false);
      } else {
        state.board.push(copy);
        events.push(createEvent(
          "appear",
          card.name + ": " + object.name + "の記憶がL" + object.lane + "/D" + depth + "へ重なった。",
          { cardId: card.id, objectId: copy.id, lane: object.lane, depth },
        ));
      }
      break;
    }
    case "dismiss": {
      const from: BoardPosition = { lane: object.lane, depth: object.depth };
      removeObject(state, object);
      events.push(createEvent(
        "exit",
        card.name + ": " + object.name + "へ、この夜より長い休暇を与えた。",
        { cardId: card.id, objectId: object.id, from },
      ));
      break;
    }
    case "rest":
      object.silenced = 1;
      object.frozen = Math.max(object.frozen, 1);
      events.push(createEvent(
        "hold",
        card.name + ": " + object.name + "は影響を止め、その深度に留まる。",
        { cardId: card.id, objectId: object.id },
      ));
      break;
  }
}

function placeObject(
  state: GameState,
  playerIndex: PlayerIndex,
  card: ObjectCardDefinition,
  laneValue: number,
  events: EventSink,
): void {
  if (!isLane(laneValue)) return;
  const object = createObject(state, card, playerIndex, laneValue, RULES.maxDepth as Depth);
  if (card.captureLaneImpactMax) {
    const currentImpact = laneImpact(state, laneValue);
    if (currentImpact === 0) {
      state.stats.fizzleCount += 1;
      events.push(createEvent(
        "fizzle",
        card.name + "は、写し取る音のないレーンへ置けなかった。",
        { cardId: card.id, lane: laneValue },
      ));
      return;
    }
    const cap = card.captureLaneImpactMax;
    object.fixedValue = Math.max(-cap, Math.min(cap, currentImpact));
    events.push(createEvent(
      "stage",
      card.name + "はレーン" + laneValue + "の影響" + signed(object.fixedValue) + "を写し取った。",
      { cardId: card.id, objectId: object.id, lane: laneValue, value: object.fixedValue },
    ));
  }
  const occupant = findAt(state, laneValue, RULES.maxDepth);
  if (occupant) {
    mergeObjects(
      state,
      object,
      occupant,
      laneValue,
      RULES.maxDepth as Depth,
      card.name,
      events,
      false,
    );
  } else {
    state.board.push(object);
    events.push(createEvent(
      "appear",
      card.name + "がレーン" + laneValue + "の深度3に現れた。",
      { objectId: object.id, lane: laneValue, depth: 3 },
    ));
  }
}

function createObject(
  state: GameState,
  card: ObjectCardDefinition,
  owner: PlayerIndex | null,
  lane: Lane,
  depth: Depth,
): DreamObjectState {
  return {
    id: "object-" + state.nextObjectId++,
    kind: card.id,
    name: card.name,
    owner,
    lane,
    depth,
    values: { ...card.values },
    fixedValue: null,
    multiplier: 1,
    potency: 0,
    temporaryPotency: 0,
    temporaryMultiplier: 1,
    silenced: 0,
    frozen: 0,
    framed: 0,
    guidedFor: [false, false],
    persistent: Boolean(card.persistent),
    returnsRemaining: card.returnsOnce ? 1 : 0,
    surfaceHoldsRemaining: Number(card.surfaceHolds || 0),
    resonanceBonus: Number(card.resonanceBonus || 0),
    mergeBonus: Number(card.mergeBonus || 0),
    origins: [card.name],
    art: card.art,
  };
}

function copyObjectAt(
  state: GameState,
  source: DreamObjectState,
  depth: Depth,
): DreamObjectState {
  return {
    ...source,
    id: "object-" + state.nextObjectId++,
    name: source.name + "の記憶",
    depth,
    values: { ...source.values },
    temporaryPotency: 0,
    temporaryMultiplier: 1,
    silenced: 0,
    frozen: 0,
    framed: 0,
    guidedFor: [false, false],
    origins: [...source.origins],
  };
}

export function injectObject(
  state: GameState,
  cardId: string,
  options: InjectObjectOptions,
): DreamObjectState | null {
  const card = getCard(cardId);
  const lane = Number(options.lane);
  const depth = Number(options.depth ?? RULES.maxDepth);
  if (!card || card.kind !== "object" || !isLane(lane) || !isDepth(depth)) return null;
  if (findAt(state, lane, depth)) return null;
  const object = createObject(state, card, options.owner ?? null, lane, depth);
  if (options.isOpeningImpression) object.isOpeningImpression = true;
  state.board.push(object);
  return object;
}

function moveObject(
  state: GameState,
  object: DreamObjectState,
  laneValue: number,
  depthValue: number,
  sourceName: string,
  events: EventSink,
): DreamObjectState | null {
  const before: BoardPosition = { lane: object.lane, depth: object.depth };
  if (depthValue <= 0) {
    exitDreamObject(state, object, sourceName, before, events);
    return null;
  }

  let destinationLane = laneValue;
  if (destinationLane < 1 || destinationLane > RULES.laneCount) {
    const stage = getStage(state.stageId);
    if (stage?.rule === "mirror-wrap") {
      destinationLane = destinationLane < 1 ? RULES.laneCount : 1;
      object.multiplier *= -1;
      state.stats.stageTriggerCount += 1;
      events.push(createEvent(
        "stage",
        stage.name + ": " + object.name + "が鏡を抜け、意味を反転してレーン" + destinationLane + "へ戻った。",
        {
          objectId: object.id,
          stageId: stage.id,
          from: before,
          to: { lane: destinationLane as Lane, depth: Math.min(RULES.maxDepth, depthValue) as Depth },
        },
      ));
    } else {
      exitDreamObject(state, object, sourceName, before, events);
      return null;
    }
  }

  const destinationDepth = Math.min(RULES.maxDepth, depthValue);
  if (!isLane(destinationLane) || !isDepth(destinationDepth)) return null;
  const occupant = findAt(state, destinationLane, destinationDepth, object.id);
  if (occupant) {
    return mergeObjects(
      state,
      object,
      occupant,
      destinationLane,
      destinationDepth,
      sourceName,
      events,
      true,
    );
  }

  object.lane = destinationLane;
  object.depth = destinationDepth;
  events.push(createEvent(
    "move",
    sourceName + ": " + object.name + "が L" + before.lane + "/D" + before.depth
      + " → L" + destinationLane + "/D" + destinationDepth + "。",
    {
      objectId: object.id,
      from: before,
      to: { lane: destinationLane, depth: destinationDepth },
    },
  ));
  return object;
}

function exitDreamObject(
  state: GameState,
  object: DreamObjectState,
  sourceName: string,
  from: BoardPosition,
  events: EventSink,
): void {
  removeObject(state, object);
  events.push(createEvent("exit", sourceName + ": " + object.name + "は夢の外へ消えた。", {
    objectId: object.id,
    from,
  }));
  if (object.returnsRemaining <= 0 || state.turn >= RULES.maxTurns) return;

  object.returnsRemaining -= 1;
  object.lane = 2;
  object.depth = RULES.maxDepth as Depth;
  state.suspendedDreams.push({
    object,
    returnTurn: state.turn + 1,
    sourceName: "赤いボール",
  });
  events.push(createEvent(
    "suspend",
    object.name + "は、次の時間に中央の深みへ帰る気配を残した。",
    { objectId: object.id, from },
  ));
}

function moveObjectsSimultaneously(
  state: GameState,
  objects: readonly DreamObjectState[],
  destinationFor: (object: DreamObjectState) => BoardPosition,
  sourceName: string,
  events: EventSink,
): void {
  let movedCount = 0;
  for (const object of objects) {
    if (!state.board.includes(object)) continue;
    const from: BoardPosition = { lane: object.lane, depth: object.depth };
    const to = destinationFor(object);
    if (from.lane === to.lane && from.depth === to.depth) continue;
    object.lane = to.lane;
    object.depth = to.depth;
    movedCount += 1;
    events.push(createEvent(
      "move",
      sourceName + ": " + object.name + "が L" + from.lane + "/D" + from.depth
        + " → L" + to.lane + "/D" + to.depth + "。",
      { objectId: object.id, from, to },
    ));
  }
  if (movedCount === 0) {
    state.stats.fizzleCount += 1;
    events.push(createEvent("fizzle", sourceName + "で動く夢はなかった。"));
    return;
  }
  resolveBoardCollisions(state, sourceName, events);
}

function resolveBoardCollisions(
  state: GameState,
  sourceName: string,
  events: EventSink,
): void {
  if (state.openSupply) {
    resolveFreeBattleCollisions(state, sourceName, events);
    return;
  }

  while (true) {
    const collision = findCollision(state);
    if (!collision) return;
    mergeObjects(
      state,
      collision[0],
      collision[1],
      collision[0].lane,
      collision[0].depth,
      sourceName,
      events,
      true,
    );
  }
}

function mergeObjects(
  state: GameState,
  moving: DreamObjectState,
  stationary: DreamObjectState,
  lane: Lane,
  depth: Depth,
  sourceName: string,
  events: EventSink,
  movingIsOnBoard: boolean,
): DreamObjectState {
  const rescued = rescueDragonOnMerge(
    state,
    moving,
    stationary,
    lane,
    depth,
    sourceName,
    events,
    movingIsOnBoard,
  );
  if (rescued) return rescued;

  if (state.openSupply) {
    return mergeFreeBattleObjects(
      state,
      [moving, stationary],
      lane,
      depth,
      sourceName,
      events,
    );
  }

  const baseValue = objectValueAtWithoutTemporary(moving, depth)
    + objectValueAtWithoutTemporary(stationary, depth);
  const mergeBonus = moving.mergeBonus + stationary.mergeBonus;
  const value = baseValue + Math.sign(baseValue) * mergeBonus;
  state.stats.mergeCount += 1;
  if (movingIsOnBoard) removeObject(state, moving);
  removeObject(state, stationary);

  const merged: DreamObjectState = {
    id: "object-" + state.nextObjectId++,
    kind: "merged",
    name: "混ざった夢",
    owner: null,
    lane,
    depth,
    values: { 3: value, 2: value, 1: value },
    fixedValue: value,
    multiplier: 1,
    potency: 0,
    temporaryPotency: 0,
    temporaryMultiplier: 1,
    silenced: 0,
    frozen: 0,
    framed: moving.framed + stationary.framed,
    guidedFor: [
      moving.guidedFor[0] || stationary.guidedFor[0],
      moving.guidedFor[1] || stationary.guidedFor[1],
    ],
    persistent: moving.persistent || stationary.persistent,
    returnsRemaining: Math.max(moving.returnsRemaining, stationary.returnsRemaining),
    surfaceHoldsRemaining: Math.max(
      moving.surfaceHoldsRemaining,
      stationary.surfaceHoldsRemaining,
    ),
    resonanceBonus: moving.resonanceBonus + stationary.resonanceBonus,
    mergeBonus,
    origins: [...moving.origins, ...stationary.origins].slice(-4),
    art: "",
  };
  state.board.push(merged);
  events.push(createEvent(
    "merge",
    sourceName + ": " + moving.name + "と" + stationary.name + "が混ざり、"
      + signed(value) + "の夢になった。",
    {
      objectId: merged.id,
      removedObjectIds: [moving.id, stationary.id],
      lane,
      depth,
      value,
      to: { lane, depth },
    },
  ));
  return merged;
}

function resolveFreeBattleCollisions(
  state: GameState,
  sourceName: string,
  events: EventSink,
): void {
  while (true) {
    const collision = findCollisionGroup(state);
    if (!collision) return;

    let ingredients = collision.objects;
    const firstAid = ingredients.find((object) => object.kind === "story_first_aid");
    const injuredDragon = ingredients.find((object) => object.kind === "story_injured_dragon");
    if (firstAid && injuredDragon) {
      const transformed = rescueDragonOnMerge(
        state,
        firstAid,
        injuredDragon,
        collision.lane,
        collision.depth,
        sourceName,
        events,
        true,
      );
      if (transformed) {
        ingredients = [
          transformed,
          ...ingredients.filter(
            (object) => object !== firstAid
              && object !== injuredDragon
              && state.board.includes(object),
          ),
        ];
      }
    }

    if (ingredients.length > 1) {
      mergeFreeBattleObjects(
        state,
        ingredients,
        collision.lane,
        collision.depth,
        sourceName,
        events,
      );
    }
  }
}

function mergeFreeBattleObjects(
  state: GameState,
  objects: readonly DreamObjectState[],
  lane: Lane,
  depth: Depth,
  sourceName: string,
  events: EventSink,
): DreamObjectState {
  const baseValue = objects.reduce(
    (sum, object) => sum + objectValueAt(object, depth),
    0,
  );
  const mergeBonus = objects.reduce((sum, object) => sum + object.mergeBonus, 0);
  const value = baseValue + Math.sign(baseValue) * mergeBonus;
  state.stats.mergeCount += 1;
  for (const object of objects) removeObject(state, object);

  // A mixed dream remembers only the visible value at the instant of fusion.
  const merged: DreamObjectState = {
    id: "object-" + state.nextObjectId++,
    kind: "merged",
    name: "混ざった夢",
    owner: null,
    lane,
    depth,
    values: { 3: value, 2: value, 1: value },
    fixedValue: value,
    multiplier: 1,
    potency: 0,
    temporaryPotency: 0,
    temporaryMultiplier: 1,
    silenced: 0,
    frozen: 0,
    framed: 0,
    guidedFor: [false, false],
    persistent: false,
    returnsRemaining: 0,
    surfaceHoldsRemaining: 0,
    resonanceBonus: 0,
    mergeBonus: 0,
    origins: objects.flatMap((object) => object.origins).slice(-4),
    art: "",
  };
  state.board.push(merged);
  events.push(createEvent(
    "merge",
    sourceName + ": " + objects.map((object) => object.name).join("、") + "が混ざり、"
      + signed(value) + "の夢になった。",
    {
      objectId: merged.id,
      removedObjectIds: objects.map((object) => object.id),
      lane,
      depth,
      value,
      to: { lane, depth },
    },
  ));
  return merged;
}

function rescueDragonOnMerge(
  state: GameState,
  moving: DreamObjectState,
  stationary: DreamObjectState,
  lane: Lane,
  depth: Depth,
  sourceName: string,
  events: EventSink,
  movingIsOnBoard: boolean,
): DreamObjectState | null {
  const kinds = new Set([moving.kind, stationary.kind]);
  if (!kinds.has("story_first_aid") || !kinds.has("story_injured_dragon")) return null;
  const flyingDragon = getCard("story_flying_dragon");
  if (!flyingDragon || flyingDragon.kind !== "object") return null;

  state.stats.mergeCount += 1;
  state.stats.stageTriggerCount += 1;
  if (movingIsOnBoard) removeObject(state, moving);
  removeObject(state, stationary);
  const transformed = createObject(state, flyingDragon, null, lane, depth);
  state.board.push(transformed);
  state.flags.dragonRescued = true;
  events.push(createEvent(
    "rescue",
    sourceName + ": 《救急道具》と《傷ついたドラゴン》が重なり、"
      + "《空を飛ぶドラゴン》へ変わった。",
    {
      objectId: transformed.id,
      removedObjectIds: [moving.id, stationary.id],
      lane,
      depth,
      to: { lane, depth },
    },
  ));
  return transformed;
}

function applyGuidance(state: GameState, events: EventSink): void {
  for (const playerIndex of PLAYER_INDICES) {
    const object = state.board.find((item) => item.guidedFor[playerIndex]);
    if (!object) continue;
    const before = state.players[playerIndex].focusLane;
    state.players[playerIndex].focusLane = object.lane;
    events.push(createEvent(
      "guidance",
      state.players[playerIndex].name + "の視線が、" + object.name + "を追ってL"
        + before + "からL" + object.lane + "へ向いた。",
      {
        playerIndex,
        objectId: object.id,
        from: { lane: before, depth: object.depth },
        to: { lane: object.lane, depth: object.depth },
      },
    ));
  }
}

function clearHourDevotionEffects(state: GameState): void {
  const objects = [
    ...state.board,
    ...state.suspendedDreams.map((entry) => entry.object),
  ];
  for (const object of objects) {
    object.temporaryPotency = 0;
    object.temporaryMultiplier = 1;
    object.silenced = 0;
    object.framed = 0;
    object.guidedFor = [false, false];
  }
}

function returnSuspendedDreams(state: GameState, events: EventSink): void {
  const returning = state.suspendedDreams.filter((entry) => entry.returnTurn <= state.turn);
  state.suspendedDreams = state.suspendedDreams.filter((entry) => entry.returnTurn > state.turn);

  if (state.openSupply) {
    for (const entry of returning) {
      const object = entry.object;
      const sourceName = entry.sourceName ?? "一刻あずかる";
      state.board.push(object);
      events.push(createEvent(
        "return",
        sourceName + ": " + object.name + "がL" + object.lane + "/D" + object.depth
          + "へ戻った。",
        {
          objectId: object.id,
          to: { lane: object.lane, depth: object.depth },
          lane: object.lane,
          depth: object.depth,
        },
      ));
    }

    if (returning.length > 0) {
      const sourceNames = new Set(returning.map((entry) => entry.sourceName ?? "一刻あずかる"));
      const sourceName = sourceNames.size === 1
        ? returning[0]?.sourceName ?? "一刻あずかる"
        : "夢の帰還";
      resolveFreeBattleCollisions(state, sourceName, events);
    }
    return;
  }

  for (const entry of returning) {
    const object = entry.object;
    const sourceName = entry.sourceName ?? "一刻あずかる";
    const occupant = findAt(state, object.lane, object.depth);
    if (occupant) {
      mergeObjects(
        state,
        object,
        occupant,
        object.lane,
        object.depth,
        sourceName,
        events,
        false,
      );
      continue;
    }
    state.board.push(object);
    events.push(createEvent(
      "return",
      sourceName + ": " + object.name + "がL" + object.lane + "/D" + object.depth
        + "へ戻った。",
      {
        objectId: object.id,
        to: { lane: object.lane, depth: object.depth },
        lane: object.lane,
        depth: object.depth,
      },
    ));
  }
}

function driftDreams(state: GameState, events: EventSink): void {
  const snapshot = [...state.board];
  for (const object of snapshot) {
    if (!state.board.includes(object)) continue;
    if (object.frozen > 0) {
      object.frozen -= 1;
      events.push(createEvent("hold", object.name + "はその深度に留まった。", {
        objectId: object.id,
        frozen: object.frozen,
      }));
      continue;
    }
    if (object.persistent) {
      events.push(createEvent("hold", object.name + "は持ち物として、その場に残った。", {
        objectId: object.id,
      }));
      continue;
    }
    if (object.depth === 1 && object.surfaceHoldsRemaining > 0) {
      object.surfaceHoldsRemaining -= 1;
      events.push(createEvent("hold", object.name + "は目覚めの手前で、もう一度だけ燃え続けた。", {
        objectId: object.id,
      }));
      continue;
    }

    const from: BoardPosition = { lane: object.lane, depth: object.depth };
    const nextDepth = object.depth - 1;
    if (nextDepth <= 0) {
      exitDreamObject(state, object, "時間経過", from, events);
    } else {
      object.depth = nextDepth as Depth;
      events.push(createEvent(
        "drift",
        object.name + "が深度" + from.depth + "から" + object.depth + "へ浮かんだ。",
        {
          objectId: object.id,
          from,
          to: { lane: object.lane, depth: object.depth },
        },
      ));
    }
  }

  resolveBoardCollisions(state, "時間経過", events);

  // A hold belongs to this hour's drift. If its object left the board before
  // drifting, do not carry the unused hold into the following hour.
  for (const entry of state.suspendedDreams) entry.object.frozen = 0;
}

function findCollision(state: GameState): [DreamObjectState, DreamObjectState] | null {
  for (let firstIndex = 0; firstIndex < state.board.length; firstIndex += 1) {
    const first = state.board[firstIndex];
    if (!first) continue;
    for (let secondIndex = firstIndex + 1; secondIndex < state.board.length; secondIndex += 1) {
      const second = state.board[secondIndex];
      if (second && first.lane === second.lane && first.depth === second.depth) {
        return [first, second];
      }
    }
  }
  return null;
}

function findCollisionGroup(
  state: GameState,
): { lane: Lane; depth: Depth; objects: DreamObjectState[] } | null {
  for (const depth of [1, 2, 3] as const) {
    for (const lane of LANES) {
      const objects = state.board.filter(
        (object) => object.lane === lane && object.depth === depth,
      );
      if (objects.length > 1) return { lane, depth, objects };
    }
  }
  return null;
}

export function laneImpact(state: GameState, lane: Lane): number {
  return contributingObjectsInLane(state, lane)
    .reduce((sum, object) => sum + objectValue(object), 0);
}

function contributingObjectsInLane(state: GameState, lane: Lane): DreamObjectState[] {
  const objects = state.board.filter((object) => object.lane === lane);
  const framed = objects.filter((object) => object.framed > 0);
  return framed.length > 0 ? framed : objects;
}

export function objectValue(object: DreamObjectState): number {
  return objectValueAt(object, object.depth);
}

export function objectValueAt(object: DreamObjectState, depth: Depth): number {
  return objectValueBeforeTemporaryMultiplier(object, depth)
    * Number(object.temporaryMultiplier || 1);
}

function objectValueBeforeTemporaryMultiplier(
  object: DreamObjectState,
  depth: Depth,
): number {
  if (object.silenced > 0) return 0;
  const value = objectValueAtWithoutTemporary(object, depth);
  const boostedMagnitude = Math.max(
    0,
    Math.abs(value) + Number(object.temporaryPotency || 0),
  );
  const magnitude = object.temporaryPotency > 0
    ? Math.min(4, boostedMagnitude)
    : boostedMagnitude;
  return Math.sign(value) * magnitude;
}

function canIgnite(object: DreamObjectState): boolean {
  const current = objectValueBeforeTemporaryMultiplier(object, object.depth);
  return current !== 0 && Math.abs(current) < 4;
}

function objectValueAtWithoutTemporary(object: DreamObjectState, depth: Depth): number {
  const base = object.fixedValue !== null
    ? Number(object.fixedValue)
    : Number(object.values[depth] || 0);
  const current = base * Number(object.multiplier || 1);
  const magnitude = Math.max(0, Math.abs(current) + Number(object.potency || 0));
  return Math.sign(current) * magnitude;
}

function changePotency(
  object: DreamObjectState,
  amount: number,
  sourceName: string,
  events: EventSink,
): void {
  const before = objectValue(object);
  object.potency += amount;
  const after = objectValue(object);
  events.push(createEvent(
    amount > 0 ? "grow" : "prune",
    sourceName + ": " + object.name + "が " + signed(before) + " → " + signed(after) + "。",
    { objectId: object.id, before, after },
  ));
}

function applyStageBeforeImpact(state: GameState, events: EventSink, resonance: boolean): void {
  const stage = getStage(state.stageId);
  if (stage?.rule !== "dragon-rescue" || !resonance) return;

  const lane = state.players[0].focusLane;
  const firstAid = state.board.find(
    (object) => object.kind === "story_first_aid" && object.lane === lane,
  );
  const injuredDragon = state.board.find(
    (object) => object.kind === "story_injured_dragon" && object.lane === lane,
  );
  if (!firstAid || !injuredDragon) return;

  const flyingDragon = getCard("story_flying_dragon");
  if (!flyingDragon || flyingDragon.kind !== "object") return;
  const depth = injuredDragon.depth;
  removeObject(state, firstAid);
  removeObject(state, injuredDragon);
  const transformed = createObject(state, flyingDragon, null, lane, depth);
  state.board.push(transformed);
  state.flags.dragonRescued = true;
  state.stats.stageTriggerCount += 1;
  events.push(createEvent(
    "rescue",
    "二人が《救急道具》を使い、《傷ついたドラゴン》は《空を飛ぶドラゴン》へ変わった。",
    {
      objectId: transformed.id,
      lane,
      depth,
      removedObjectIds: [firstAid.id, injuredDragon.id],
    },
  ));
}

function applyStageAfterImpact(state: GameState, events: EventSink): void {
  const stage = getStage(state.stageId);
  if (stage?.rule !== "cultivate") return;

  const focusedLanes = new Set<Lane>(
    state.players.map((player) => player.focusLane),
  );
  for (const lane of focusedLanes) {
    const target = state.board
      .filter((object) => object.lane === lane && objectValue(object) !== 0)
      .sort((first, second) => first.depth - second.depth || first.id.localeCompare(second.id))[0];
    if (!target) continue;
    state.stats.stageTriggerCount += 1;
    changePotency(target, 1, stage.name, events);
  }
}

function findAt(
  state: GameState,
  lane: number,
  depth: number,
  exceptId = "",
): DreamObjectState | null {
  return state.board.find(
    (object) => object.id !== exceptId && object.lane === lane && object.depth === depth,
  ) ?? null;
}

function removeObject(state: GameState, target: DreamObjectState): void {
  state.board = state.board.filter((object) => object !== target);
}

function checkNightmare(state: GameState, events: EventSink): void {
  const firstLost = state.players[0].happiness <= RULES.nightmareLine;
  const secondLost = state.players[1].happiness <= RULES.nightmareLine;
  if (!firstLost && !secondLost) return;
  if (firstLost && secondLost) {
    setResult(state, "draw", "二人とも悪夢から目覚めた", "重なった夢は同時に砕けた。", events, "nightmare");
  } else if (firstLost) {
    setResult(
      state,
      "p2",
      state.players[0].name + "が悪夢から目覚めた",
      state.players[1].name + "の勝利。",
      events,
      "nightmare",
    );
  } else {
    setResult(
      state,
      "p1",
      state.players[1].name + "が悪夢から目覚めた",
      state.players[0].name + "の勝利。",
      events,
      "nightmare",
    );
  }
}

function finishMorning(state: GameState, events: EventSink): void {
  const firstOverslept = state.players[0].happiness >= RULES.oversleepLine;
  const secondOverslept = state.players[1].happiness >= RULES.oversleepLine;
  if (firstOverslept && secondOverslept) {
    setResult(state, "draw", "二人とも寝坊した", "甘すぎる夢に、夜明けが置き去りにされた。", events, "oversleep");
    return;
  }
  if (firstOverslept) {
    setResult(
      state,
      "p2",
      state.players[0].name + "が寝坊した",
      state.players[1].name + "の勝利。",
      events,
      "oversleep",
    );
    return;
  }
  if (secondOverslept) {
    setResult(
      state,
      "p1",
      state.players[1].name + "が寝坊した",
      state.players[0].name + "の勝利。",
      events,
      "oversleep",
    );
    return;
  }

  const firstDistance = Math.abs(state.players[0].happiness - RULES.idealHappiness);
  const secondDistance = Math.abs(state.players[1].happiness - RULES.idealHappiness);
  if (firstDistance === secondDistance) {
    setResult(state, "draw", "同じ朝へたどり着いた", "二つの幸福は、理想から同じ距離にある。", events);
  } else if (firstDistance < secondDistance) {
    setResult(
      state,
      "p1",
      state.players[0].name + "がよりよい朝を迎えた",
      "理想との差は" + firstDistance + "。",
      events,
    );
  } else {
    setResult(
      state,
      "p2",
      state.players[1].name + "がよりよい朝を迎えた",
      "理想との差は" + secondDistance + "。",
      events,
    );
  }
}

function setResult(
  state: GameState,
  winner: Winner,
  title: string,
  detail: string,
  events: EventSink,
  cause: ResultCause = "morning",
): void {
  const result: GameResult = { winner, title, detail, cause };
  state.result = result;
  events.push(createEvent("result", title + "。" + detail, { winner }));
}

export function chooseAiPlan(state: GameState, playerIndex: PlayerIndex = 1): PlayerPlan {
  const player = state.players[playerIndex];
  const candidates: AiCandidate[] = [];
  const focusLanes = legalFocusLanes(state, playerIndex);
  const silentEvents = createSilentEventSink();

  if (state.openSupply) {
    for (const entry of state.openSupply.cards) {
      if (!entry.availableTo[playerIndex] || state.openSupply.energy[playerIndex] < entry.cost) {
        continue;
      }
      const card = getCard(entry.cardId);
      if (!card) continue;
      for (const target of getCardTargets(state, card.id)) {
        for (const focusLane of focusLanes) {
          const simulated = cloneState(state);
          applyCard(simulated, playerIndex, card, target, silentEvents);
          let score = scoreAiPosition(state, simulated, playerIndex, focusLane) - entry.cost * 0.12;
          if (entry.limited && state.turn <= 2) score -= 0.35;
          candidates.push({
            score,
            plan: {
              focusLane,
              cardInstanceId: "",
              supplyCardId: entry.cardId,
              devotionId: "",
              target,
            },
          });
        }
      }
    }
  }

  for (const instance of player.hand) {
    const card = getCard(instance.cardId);
    if (!card) continue;
    for (const target of getCardTargets(state, card.id)) {
      for (const focusLane of focusLanes) {
        const simulated = cloneState(state);
        applyCard(simulated, playerIndex, card, target, silentEvents);
        const score = scoreAiPosition(state, simulated, playerIndex, focusLane);
        candidates.push({
          score,
          plan: {
            focusLane,
            cardInstanceId: instance.instanceId,
            devotionId: "",
            target,
          },
        });
      }
    }
  }

  if (!player.devotionUsed) {
    const servant = getServant(player.servantId);
    if (servant) {
      for (const target of getDevotionTargets(state, playerIndex)) {
        for (const focusLane of focusLanes) {
          const simulated = cloneState(state);
          resolveDevotion(simulated, playerIndex, {
            focusLane,
            cardInstanceId: "",
            devotionId: servant.devotion.id,
            target,
          }, silentEvents);
          let score = scoreAiPosition(state, simulated, playerIndex, focusLane) - 1.5;
          if (servant.devotion.effect === "preserve" && state.turn < RULES.maxTurns) {
            const targetObject = state.board.find((object) => object.id === target.objectId);
            score += Math.abs(targetObject ? objectValue(targetObject) : 0) * 0.55;
          }
          candidates.push({
            score,
            plan: {
              focusLane,
              cardInstanceId: "",
              devotionId: servant.devotion.id,
              target,
            },
          });
        }
      }
    }
  }

  for (const focusLane of focusLanes) {
    candidates.push({
      score: scoreAiPosition(state, state, playerIndex, focusLane) - 0.4,
      plan: passPlan(focusLane),
    });
  }

  if (candidates.length === 0) return passPlan(focusLanes[0] ?? player.focusLane);
  candidates.sort((first, second) => second.score - first.score);
  return candidates[0]?.plan ?? passPlan(player.focusLane);
}

function scoreAiPosition(
  randomStateOwner: GameState,
  simulated: GameState,
  playerIndex: PlayerIndex,
  focusLane: Lane,
): number {
  const opponentIndex: PlayerIndex = playerIndex === 0 ? 1 : 0;
  const ownGuided = simulated.board.find((object) => object.guidedFor[playerIndex]);
  const ownFocus = ownGuided?.lane ?? focusLane;
  let ownRaw = laneImpact(simulated, ownFocus);
  if (ownGuided && contributingObjectsInLane(simulated, ownFocus).includes(ownGuided)) {
    ownRaw += Math.sign(objectValue(ownGuided));
  }
  const opponentGuided = simulated.board.find((object) => object.guidedFor[opponentIndex]);
  const opponentFocus = opponentGuided?.lane ?? simulated.players[opponentIndex].focusLane;
  const sameLane = ownFocus === opponentFocus;
  const ownImpact = sameLane ? ownRaw + Math.sign(ownRaw) : ownRaw;
  let opponentRaw = laneImpact(simulated, opponentFocus);
  if (opponentGuided && contributingObjectsInLane(simulated, opponentFocus).includes(opponentGuided)) {
    opponentRaw += Math.sign(objectValue(opponentGuided));
  }
  const opponentImpact = sameLane ? opponentRaw + Math.sign(opponentRaw) : opponentRaw;
  const ownAfter = simulated.players[playerIndex].happiness + ownImpact;
  const opponentAfter = simulated.players[opponentIndex].happiness + opponentImpact;
  const finalTurn = simulated.turn >= RULES.maxTurns;
  let score = morningUtility(ownAfter, finalTurn) - morningUtility(opponentAfter, finalTurn) * 0.42;
  if (sameLane) score += Math.abs(ownImpact) * 0.15;
  return score + nextRandom(randomStateOwner) * 0.04;
}

function morningUtility(happiness: number, finalTurn: boolean): number {
  if (happiness <= RULES.nightmareLine) return -220 - Math.abs(happiness) * 10;
  if (finalTurn && happiness >= RULES.oversleepLine) {
    return -180 - (happiness - RULES.oversleepLine) * 10;
  }
  return -Math.abs(happiness - RULES.idealHappiness) * 8
    - Math.max(0, happiness - RULES.idealHappiness) * 2;
}

export function cloneState(state: GameState): GameState {
  return JSON.parse(JSON.stringify(state)) as GameState;
}

function createEvent(
  type: ResolutionEventType,
  text: string,
  data: Omit<UnframedResolutionEvent, "type" | "text"> = {},
): UnframedResolutionEvent {
  return { type, text, ...data };
}

function createEventQueue(state: GameState): EventQueue {
  const items: ResolutionEvent[] = [];
  return {
    items,
    push: (...events: UnframedResolutionEvent[]): number => {
      for (const item of events) {
        items.push({ ...item, frame: captureResolutionFrame(state) });
      }
      return items.length;
    },
  };
}

function createSilentEventSink(): EventSink {
  return { push: (...events: UnframedResolutionEvent[]) => events.length };
}

export function captureResolutionFrame(state: GameState): ResolutionFrame {
  return {
    turn: state.turn,
    firstPlayer: state.firstPlayer,
    players: [
      capturePlayerFrame(state, 0),
      capturePlayerFrame(state, 1),
    ],
    board: state.board.map(cloneObject),
  };
}

function capturePlayerFrame(state: GameState, playerIndex: PlayerIndex) {
  const player = state.players[playerIndex];
  const frame = {
    name: player.name,
    servantId: player.servantId,
    happiness: player.happiness,
    focusLane: player.focusLane,
    handCount: player.hand.length,
  };
  return state.openSupply
    ? { ...frame, energy: state.openSupply.energy[playerIndex] }
    : frame;
}

function cloneObject(object: DreamObjectState): DreamObjectState {
  return {
    ...object,
    values: { ...object.values },
    origins: [...object.origins],
    guidedFor: [...object.guidedFor] as [boolean, boolean],
  };
}

export function signed(value: number): string {
  return value > 0 ? "+" + value : String(value);
}

function isLane(value: number): value is Lane {
  return value === 1 || value === 2 || value === 3;
}

function isDepth(value: number): value is Depth {
  return value === 1 || value === 2 || value === 3;
}

function isDirection(value: number): value is Direction {
  return value === -1 || value === 1;
}

function normalizeSeed(seed: number): number {
  const normalized = Number(seed) >>> 0;
  return normalized || 0x9e3779b9;
}

function nextRandom(state: GameState): number {
  state.randomState = (Math.imul(state.randomState, 1664525) + 1013904223) >>> 0;
  return state.randomState / 0x100000000;
}

function shuffle<T>(state: GameState, list: T[]): void {
  for (let index = list.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(nextRandom(state) * (index + 1));
    const first = list[index];
    const second = list[swapIndex];
    if (first === undefined || second === undefined) continue;
    list[index] = second;
    list[swapIndex] = first;
  }
}
